export interface RowQuestionContext {
  gene?: string;
  disease?: string;
  variant?: string;
  chemical?: string;
  score?: number;
  associationType?: string;
}

function pickString(row: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') return String(value).trim();
  }
  return undefined;
}

export function extractRowQuestionContext(
  row: Record<string, unknown> | undefined | null,
): RowQuestionContext {
  if (!row) return {};

  const chemNames = row.chemNames;
  const chemicalFromArray =
    Array.isArray(chemNames) && chemNames.length > 0 ? String(chemNames[0]).trim() : undefined;

  return {
    gene: pickString(row, ['geneSymbol', 'symbol']),
    disease: pickString(row, ['diseaseName', 'disease1_Name', 'disease2_Name']),
    variant: pickString(row, ['variantStrID', 'strID', 'variant']),
    chemical: pickString(row, ['chemPrefName', 'chemName']) ?? chemicalFromArray,
    score: typeof row.score === 'number' ? row.score : undefined,
    associationType: pickString(row, ['associationType']),
  };
}

export const ROW_QUESTION_DISPLAY_COUNT = 5;

export function pickRandomItems<T>(items: T[], count: number): T[] {
  if (items.length === 0) return [];
  if (count >= items.length) return [...items];

  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled.slice(0, count);
}

export function buildRowQuestionTemplates(row: Record<string, unknown> | undefined | null): string[] {
  const ctx = extractRowQuestionContext(row);
  const { gene, disease, variant, chemical, score, associationType } = ctx;

  if (gene && disease) {
    const templates = [
      `What evidence supports the association between ${gene} and ${disease}?`,
      `How strong is the association between ${gene} and ${disease}?`,
      `What variants of ${gene} are linked to ${disease}?`,
      `What biological mechanisms might link ${gene} to ${disease}?`,
      `Are there conflicting findings about ${gene} and ${disease}?`,
      `What publications support the ${gene}–${disease} association?`,
      `Summarize the association between ${gene} and ${disease}.`,
    ];
    if (score != null) {
      templates.push(`How strong is the association between ${gene} and ${disease} (score: ${score})?`);
    }
    if (associationType) {
      templates.push(`What does the ${associationType} evidence tell us about ${gene} and ${disease}?`);
    }
    return templates;
  }

  if (variant && disease) {
    return [
      `What evidence links ${variant} to ${disease}?`,
      `How strong is the association between ${variant} and ${disease}?`,
      gene ? `What gene does ${variant} in ${gene} affect?` : `Which gene is affected by ${variant}?`,
      `What is the functional impact of ${variant}?`,
      `What populations show this ${variant}–${disease} association?`,
      `Summarize the association between ${variant} and ${disease}.`,
    ];
  }

  if (gene) {
    return [
      `Which diseases are associated with ${gene}?`,
      `What variants are linked to ${gene}?`,
      `What is the disease specificity index (DSI) of ${gene}?`,
      `What  chemicals and drugs are frequently associated with ${gene}?`,
      `Show me recent publications investigating ${gene}`,
    ];
  }

  if (disease) {
    return [
      `What genes are associated with ${disease}?`,
      `What is known about the genetic basis of ${disease}?`,
      `What variants are linked to ${disease}?`,
      `Which biomarkers are relevant to ${disease}?`,
      `Summarize what we know about the genetic basis of ${disease}.`,
    ];
  }

  if (chemical) {
    return [
      `What genes are targeted by ${chemical}?`,
      `Which diseases are associated with ${chemical}?`,
      `What is the mechanism of action of ${chemical}?`,
      `What clinical evidence exists for ${chemical}?`,
      `What adverse effects are linked to ${chemical}?`,
      `Summarize what we know about ${chemical}.`,
    ];
  }

  return [
    'What can you tell me about this record?',
    'What are the key associations in this data?',
    'What evidence supports these findings?',
    'What related entities should I explore?',
    'How reliable is this association?',
    'What should I investigate next from this record?',
    'Summarize this record.',
  ];
}

export function buildRandomRowQuestionTemplates(
  row: Record<string, unknown> | undefined | null,
  count: number = ROW_QUESTION_DISPLAY_COUNT,
): string[] {
  return pickRandomItems(buildRowQuestionTemplates(row), count);
}

export function buildRowContextMessage(row: Record<string, unknown> | undefined | null): string {
  const ctx = extractRowQuestionContext(row);
  const lines: string[] = [];

  if (ctx.gene) lines.push(`Gene: ${ctx.gene}`);
  if (ctx.disease) lines.push(`Disease: ${ctx.disease}`);
  if (ctx.variant) lines.push(`Variant: ${ctx.variant}`);
  if (ctx.chemical) lines.push(`Chemical: ${ctx.chemical}`);
  if (ctx.score != null) lines.push(`Score: ${ctx.score}`);
  if (ctx.associationType) lines.push(`Association type: ${ctx.associationType}`);

  if (lines.length === 0) {
    return "I'm looking at a record from the DISGENET results table. Please help me analyze it.";
  }

  return `I'm looking at this record from the DISGENET results table:\n${lines.join('\n')}\n\n`;
}
